import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend
} from 'recharts';
import api from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtK = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${n}`;
const MESES_NOMBRES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

export default function AdminReportes() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [ingresos, setIngresos] = useState(null);
  const [retencion, setRetencion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/reportes/ingresos?anio=${anio}`),
      api.get('/admin/reportes/retencion'),
    ]).then(([i, r]) => {
      setIngresos(i.data);
      setRetencion(r.data);
    }).finally(() => setLoading(false));
  }, [anio]);

  const exportarCSV = () => {
    if (!ingresos?.meses?.length) return;
    const headers = ['Mes','Ingresos','Pendiente','Reembolsado','N° Pagos','Clientes que pagaron'];
    const rows = ingresos.meses.map(m => {
      const [, mes] = m.mes.split('-');
      return [MESES_NOMBRES[parseInt(mes)], m.ingresos, m.pendiente, m.reembolsado, m.num_pagos, m.clientes_pagaron];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_ingresos_${anio}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Cargando...</div>;

  const mesData = (ingresos?.meses || []).map(m => {
    const [, mes] = m.mes.split('-');
    return { ...m, label: MESES_NOMBRES[parseInt(mes)] || m.mes };
  });

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>Reportes</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-3)', fontSize: 14 }}>Análisis financiero y de retención</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            value={anio}
            onChange={e => setAnio(Number(e.target.value))}
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-1s)', fontSize: 14, outline: 'none' }}
          >
            {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={exportarCSV} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Annual summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[
          { label: `Total Ingresos ${anio}`, value: fmt(ingresos?.ingresos_total), color: '#34d399', icon: '💰' },
          { label: 'Pendiente de cobro', value: fmt(ingresos?.pendiente_total), color: '#fbbf24', icon: '⏳' },
          { label: 'Clientes activos', value: retencion?.activos, color: '#6366f1', icon: '🏢' },
          { label: 'Cancelados', value: retencion?.cancelados, color: '#f87171', icon: '❌' },
          { label: 'Upgrades históricos', value: retencion?.upgrades, color: '#22d3ee', icon: '↑' },
          { label: 'Downgrades históricos', value: retencion?.downgrades, color: '#f97316', icon: '↓' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '18px 20px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</span>
              <span style={{ fontSize: 18 }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>Ingresos mensuales {anio}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={mesData} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtK} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}
              labelStyle={{ color: 'var(--text-2)' }}
              formatter={(v, name) => [fmt(v), name === 'ingresos' ? 'Ingresos' : name === 'pendiente' ? 'Pendiente' : 'Reembolsado']}
            />
            <Legend formatter={(v) => v === 'ingresos' ? 'Ingresos' : v === 'pendiente' ? 'Pendiente' : 'Reembolsado'} wrapperStyle={{ fontSize: 12, color: 'var(--text-3)' }} />
            <Bar dataKey="ingresos" fill="#34d399" radius={[4,4,0,0]} name="ingresos" />
            <Bar dataKey="pendiente" fill="#fbbf24" radius={[4,4,0,0]} name="pendiente" />
            <Bar dataKey="reembolsado" fill="#f87171" radius={[4,4,0,0]} name="reembolsado" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Churn + Client count */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Churn history */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>Churn mensual</h3>
          {retencion?.churnMensual?.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={[...retencion.churnMensual].reverse().map(m => {
                const [, mes] = m.mes.split('-');
                return { ...m, label: MESES_NOMBRES[parseInt(mes)] || m.mes };
              })}>
                <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} labelStyle={{ color: 'var(--text-2)' }} />
                <Line type="monotone" dataKey="cancelaciones" stroke="#f87171" strokeWidth={2} dot={{ fill: '#f87171' }} name="Cancelaciones" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-4)', paddingTop: 30 }}>Sin datos de churn</div>
          )}
        </div>

        {/* Status breakdown */}
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>Distribución de clientes</h3>
          {retencion && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Activos', value: retencion.activos, color: '#34d399' },
                { label: 'Trial', value: retencion.trial, color: '#fbbf24' },
                { label: 'Prospectos', value: retencion.prospectos, color: '#6366f1' },
                { label: 'Suspendidos', value: retencion.suspendidos, color: '#f97316' },
                { label: 'Cancelados', value: retencion.cancelados, color: '#f87171' },
              ].map((s, i) => {
                const totalClientes = retencion.activos + retencion.trial + retencion.prospectos + retencion.suspendidos + retencion.cancelados;
                const pct = totalClientes ? Math.round((s.value / totalClientes) * 100) : 0;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: 'var(--text-2)' }}>{s.label}</span>
                      <span style={{ color: s.color, fontWeight: 600 }}>{s.value} ({pct}%)</span>
                    </div>
                    <div style={{ background: 'var(--bg-app)', borderRadius: 4, height: 6 }}>
                      <div style={{ width: `${pct}%`, background: s.color, height: 6, borderRadius: 4, transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tabla detallada */}
      {mesData.length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 24, border: '1px solid var(--border)', marginTop: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--text-2)' }}>Detalle mensual {anio}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)' }}>
                {['Mes','Ingresos','Pendiente','Reembolsado','N° Pagos','Clientes que pagaron'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mesData.map((m, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--text-1s)', fontWeight: 600 }}>{m.label} {anio}</td>
                  <td style={{ padding: '10px 14px', color: '#34d399', fontWeight: 600 }}>{fmt(m.ingresos)}</td>
                  <td style={{ padding: '10px 14px', color: m.pendiente > 0 ? '#fbbf24' : '#475569' }}>{fmt(m.pendiente)}</td>
                  <td style={{ padding: '10px 14px', color: m.reembolsado > 0 ? '#f87171' : '#475569' }}>{fmt(m.reembolsado)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-2)', textAlign: 'center' }}>{m.num_pagos}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-2)', textAlign: 'center' }}>{m.clientes_pagaron}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-app)' }}>
                <td style={{ padding: '12px 14px', color: 'var(--text-1)', fontWeight: 700 }}>TOTAL {anio}</td>
                <td style={{ padding: '12px 14px', color: '#34d399', fontWeight: 700 }}>{fmt(ingresos?.ingresos_total)}</td>
                <td style={{ padding: '12px 14px', color: '#fbbf24', fontWeight: 700 }}>{fmt(ingresos?.pendiente_total)}</td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
